import { useCallback, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import GetAppIcon from '@material-ui/icons/GetApp';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { downloadPng, downloadSvg } from '../lib/exportSvg';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
}));

export interface C4ExportButtonsProps {
  getSvg: () => SVGSVGElement | null;
  fileName: string;
  className?: string;
}

export function C4ExportButtons({
  getSvg,
  fileName,
  className,
}: C4ExportButtonsProps) {
  const classes = useStyles();
  const theme = useTheme();
  const errorApi = useApi(errorApiRef);
  const [busy, setBusy] = useState(false);

  const background = theme.palette.background.paper;

  const withSvg = useCallback(
    async (action: (svg: SVGSVGElement) => void | Promise<void>) => {
      const svg = getSvg();
      if (!svg) {
        errorApi.post(
          new Error('Nada para exportar: o desenho ainda não foi renderizado.'),
        );
        return;
      }

      setBusy(true);
      try {
        await action(svg);
      } catch (error) {
        errorApi.post(
          error instanceof Error ? error : new Error(String(error)),
        );
      } finally {
        setBusy(false);
      }
    },
    [errorApi, getSvg],
  );

  return (
    <Box
      className={className ? `${classes.root} ${className}` : classes.root}
      data-testid="c4-export-buttons"
    >
      <Tooltip title="Baixa o desenho como SVG — vetor, abre no Figma e no navegador">
        <Button
          size="small"
          variant="outlined"
          startIcon={<GetAppIcon />}
          disabled={busy}
          onClick={() =>
            withSvg(svg => downloadSvg(svg, fileName, { background }))
          }
        >
          Exportar SVG
        </Button>
      </Tooltip>
      <Tooltip title="Baixa o desenho como PNG em 2x — para colar em slide ou documento">
        <Button
          size="small"
          variant="outlined"
          startIcon={<GetAppIcon />}
          disabled={busy}
          onClick={() =>
            withSvg(svg => downloadPng(svg, fileName, { background }))
          }
        >
          Exportar PNG
        </Button>
      </Tooltip>
    </Box>
  );
}
